import { FeedFilterValues } from '@capsicum/common';
import { type FeedFiltersType, usePartners, useWalletsBalanceWrapper } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import { filterAtomTypeMap } from '../../data-access';
import { type FeedFiltersAtomType, FeedFiltersDimension, FeedFiltersDimensionMapping } from '../../models';

type UsePostFilterMySociosProps = {
	type: FeedFiltersType;
};
type PostFilterMySociosAdditionalParams = {
	selectedPartnersIds?: Array<string>;
};

export const usePostFilterMySocios = ({ type }: UsePostFilterMySociosProps) => {
	const [filters, setFilters] = useAtom(filterAtomTypeMap[type]);
	const { currencies } = useWalletsBalanceWrapper({ removeExcludedCurrencies: true });

	const { getPartnerBy } = usePartners();
	const ssuPartner = useMemo(() => getPartnerBy(p => p.shortName === 'SSU'), [getPartnerBy]);

	const mixPartnerDimensionFilter = (
		baseFilter: FeedFiltersAtomType,
		filter: FeedFilterValues,
		partnersIds: Array<string>
	): FeedFiltersAtomType => {
		const newFilters = baseFilter.selectedFilters.filter(
			x =>
				!FeedFiltersDimensionMapping.get(FeedFiltersDimension.PARTNER)!.includes(x) &&
				x !== FeedFilterValues.ALL
		);

		newFilters.push(filter);

		return {
			partners: partnersIds,
			selectedFilters: newFilters
		};
	};

	const mixPostTypeDimensionFilter = (
		baseFilter: FeedFiltersAtomType,
		filter: FeedFilterValues
	): FeedFiltersAtomType => {
		const newFilters = baseFilter.selectedFilters.filter(
			x =>
				!FeedFiltersDimensionMapping.get(FeedFiltersDimension.POST_TYPE)!.includes(x) &&
				x !== FeedFilterValues.ALL
		);

		newFilters.push(filter);

		return {
			partners: baseFilter.partners,
			selectedFilters: newFilters
		};
	};

	const getPartnersByFilterType = (type: FeedFilterValues): Array<string> => {
		switch (type) {
			case FeedFilterValues.SSU:
				return [ssuPartner?.id || ''];
			case FeedFilterValues.WITH_TOKEN:
				return (() => {
					const currenciesWithBalance = currencies?.filter(c => Number(c.totalAmount) > 0) || [];
					const partnersIdsWithBalance = currenciesWithBalance.map(
						c => getPartnerBy(p => p.currencyId === c.currency.id)?.id || ''
					);

					return partnersIdsWithBalance.length ? partnersIdsWithBalance : [''];
				})();
		}

		return [];
	};

	const removeFilter = (baseFilter: FeedFiltersAtomType, filter: FeedFilterValues): FeedFiltersAtomType => {
		const newFilters = baseFilter.selectedFilters.filter(x => x !== filter);

		const newPartners = FeedFiltersDimensionMapping.get(FeedFiltersDimension.PARTNER)!.includes(filter)
			? newFilters.map(x => getPartnersByFilterType(x)).flat()
			: baseFilter.partners;

		return {
			partners: FeedFiltersDimensionMapping.get(FeedFiltersDimension.PARTNER)!.includes(filter)
				? newPartners
				: baseFilter.partners,
			selectedFilters: newFilters
		};
	};

	const handleOnFilterSelected = (
		selectedFilterType: FeedFilterValues,
		additionalParams?: PostFilterMySociosAdditionalParams
	): Array<string> => {
		const previousFilters = { ...filters };
		let newFilters: { partners?: Array<string>; selectedFilters: Array<FeedFilterValues> };

		switch (selectedFilterType) {
			case FeedFilterValues.VIEW_BY_TEAM:
				if (additionalParams!.selectedPartnersIds?.length === 0) {
					newFilters = removeFilter(previousFilters, FeedFilterValues.VIEW_BY_TEAM);
					break;
				}
				newFilters = mixPartnerDimensionFilter(
					previousFilters,
					FeedFilterValues.VIEW_BY_TEAM,
					additionalParams!.selectedPartnersIds!
				);
				break;

			case FeedFilterValues.SSU:
				if (previousFilters.selectedFilters.includes(FeedFilterValues.SSU)) {
					newFilters = removeFilter(previousFilters, FeedFilterValues.SSU);
					break;
				}
				newFilters = mixPartnerDimensionFilter(previousFilters, FeedFilterValues.SSU, [ssuPartner?.id || '']);
				break;

			case FeedFilterValues.WITH_TOKEN:
				if (previousFilters.selectedFilters.includes(FeedFilterValues.WITH_TOKEN)) {
					newFilters = removeFilter(previousFilters, FeedFilterValues.WITH_TOKEN);
					break;
				}
				newFilters = mixPartnerDimensionFilter(
					previousFilters,
					FeedFilterValues.WITH_TOKEN,
					getPartnersByFilterType(FeedFilterValues.WITH_TOKEN)
				);
				break;

			case FeedFilterValues.ALL:
				newFilters = { partners: [], selectedFilters: [FeedFilterValues.ALL] };
				break;

			default:
				if (previousFilters.selectedFilters.includes(selectedFilterType)) {
					newFilters = removeFilter(previousFilters, selectedFilterType);
				} else {
					newFilters = mixPostTypeDimensionFilter(previousFilters, selectedFilterType);
				}
		}
		if (!newFilters.selectedFilters.length) {
			newFilters.selectedFilters = [FeedFilterValues.ALL];
		}

		setFilters(newFilters);

		return newFilters.selectedFilters;
	};

	return { handleOnFilterSelected };
};
